"use client";

import { useEffect, useRef, useState } from "react";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import defaultInputStyles from "@/app/(components)/generics/inputs/styles/DefaultInput.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import styles from "./AddressInput.module.css";

type AddressFeature = {
  properties: {
    label: string;
    name: string;
    city: string;
    postcode: string;
  };
};

type AddressSuggestion = {
  label: string;
  address: string;
  city: string;
  postalCode: string;
};

type Props = {
  address: string;
  city: string;
  postalCode: string;
  country: string;
  onChange: (address: string, city: string, postalCode: string, country: string) => void;
  disabled?: boolean;
  lang?: string;
  showRequiredLabel?: boolean;
  addressError?: string;
  cityError?: string;
  postalError?: string;
  countryError?: string;
};

async function fetchAddressSuggestions(query: string): Promise<AddressSuggestion[]> {
  const res = await fetch(
    `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=6`,
  );
  if (!res.ok) return [];
  const data: { features: AddressFeature[] } = await res.json();
  return data.features.map((f) => ({
    label: f.properties.label,
    address: f.properties.name,
    city: f.properties.city,
    postalCode: f.properties.postcode,
  }));
}

export default function AddressInput(props: Props) {
  const {
    address,
    city,
    postalCode,
    country,
    onChange,
    disabled,
    lang,
    showRequiredLabel,
    addressError,
    cityError,
    postalError,
    countryError,
  } = props;

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleAddressChange = (val: string) => {
    onChange(val, city, postalCode, country);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const results = await fetchAddressSuggestions(val);
      setSuggestions(results);
      setOpen(results.length > 0);
    }, 300);
  };

  const handleSelect = (s: AddressSuggestion) => {
    onChange(s.address, s.city, s.postalCode, "France");
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      {/* Address */}
      <div className={styles.field}>
        <label className={inputStyles.label}>
          {T(TKey.PRACTICE_ADDRESS, lang)}
          {showRequiredLabel && <span className={inputStyles.labelRequired}>*</span>}
        </label>
        <input
          className={defaultInputStyles.input}
          type="text"
          value={address}
          placeholder={T(TKey.PRACTICE_ADDRESS, lang) as string}
          disabled={disabled}
          onChange={(e) => handleAddressChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
        />
        {open && (
          <div className={styles.dropdown}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" className={styles.item} onClick={() => handleSelect(s)}>
                <span className={styles.itemAddress}>{s.address}</span>
                <span className={styles.itemMeta}>
                  {s.city} {s.postalCode}
                </span>
              </button>
            ))}
          </div>
        )}
        {addressError && (
          <div className={defaultInputStyles.errorMessage}>
            <ErrorMessage message={addressError} />
          </div>
        )}
      </div>

      {/* City + Postal */}
      <div className={styles.cityRow}>
        <div className={styles.field}>
          <label className={inputStyles.label}>
            {T(TKey.PRACTICE_CITY, lang)}
            {showRequiredLabel && <span className={inputStyles.labelRequired}>*</span>}
          </label>
          <input
            className={defaultInputStyles.input}
            type="text"
            value={city}
            placeholder={T(TKey.PRACTICE_CITY, lang) as string}
            disabled={disabled}
            onChange={(e) => onChange(address, e.target.value, postalCode, country)}
          />
          {cityError && (
            <div className={defaultInputStyles.errorMessage}>
              <ErrorMessage message={cityError} />
            </div>
          )}
        </div>

        <div className={styles.postalField}>
          <label className={inputStyles.label}>
            {T(TKey.PRACTICE_POSTAL_CODE, lang)}
            {showRequiredLabel && <span className={inputStyles.labelRequired}>*</span>}
          </label>
          <input
            className={defaultInputStyles.input}
            type="text"
            value={postalCode}
            placeholder="75001"
            disabled={disabled}
            onChange={(e) => onChange(address, city, e.target.value, country)}
          />
          {postalError && (
            <div className={defaultInputStyles.errorMessage}>
              <ErrorMessage message={postalError} />
            </div>
          )}
        </div>
      </div>

      {/* Country */}
      <div className={styles.field}>
        <label className={inputStyles.label}>
          {T(TKey.PRACTICE_COUNTRY, lang)}
          {showRequiredLabel && <span className={inputStyles.labelRequired}>*</span>}
        </label>
        <input
          className={defaultInputStyles.input}
          type="text"
          value={country}
          placeholder={T(TKey.PRACTICE_COUNTRY, lang) as string}
          disabled={disabled}
          onChange={(e) => onChange(address, city, postalCode, e.target.value)}
        />
        {countryError && (
          <div className={defaultInputStyles.errorMessage}>
            <ErrorMessage message={countryError} />
          </div>
        )}
      </div>
    </div>
  );
}
